"use strict";
/*
 * Copyright 2022, Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importDefault(require("react"));
const react_i18next_1 = require("react-i18next");
const api_1 = require("@react-google-maps/api");
const bowser_1 = __importDefault(require("bowser"));
const googleMaps_config_1 = require("../../../../config/googleMaps.config");
const InputLoading_1 = __importDefault(require("../../InputLoading"));
const GoogleMapUtils_1 = require("./GoogleMapUtils");
const errorHandling_1 = require("../../../../services/errorManagement/errorHandling");
const callWithBounds = (bounds, call) => {
    if (!call) {
        return;
    }
    call(bounds
        ? [
            bounds.getSouthWest().lat(),
            bounds.getSouthWest().lng(),
            bounds.getNorthEast().lat(),
            bounds.getNorthEast().lng()
        ]
        : undefined);
};
const InputMapGoogle = (props) => {
    // Set the google map config once, as it cannot be changed after it is
    // loaded (for language change for example). see
    // https://stackoverflow.com/questions/7065420/how-can-i-change-the-language-of-google-maps-on-the-run
    const googleMapConfig = react_1.default.useMemo(() => (0, googleMaps_config_1.getCurrentGoogleMapConfig)(props.i18n.language), []);
    const { isLoaded, loadError } = (0, api_1.useJsApiLoader)(googleMapConfig);
    if (loadError !== undefined) {
        const browserTechData = bowser_1.default.getParser(window.navigator.userAgent).parse();
        const errorMessage = `Google Maps API could not be loaded. Browser: ${JSON.stringify(browserTechData)}, error: ${loadError.message}`;
        (0, errorHandling_1.logClientSideMessage)(errorMessage);
    }
    const [map, setMap] = react_1.default.useState(null);
    const [center, setCenter] = react_1.default.useState({
        lat: props.defaultCenter.lat,
        lng: props.defaultCenter.lon
    });
    const [placesInfoWindow, setPlacesInfoWindow] = react_1.default.useState(undefined);
    const changeValueAndPan = react_1.default.useCallback((feature) => {
        props.onValueChange(feature);
        if (feature) {
            setCenter({ lat: feature.geometry.coordinates[1], lng: feature.geometry.coordinates[0] });
            map.panTo({
                lat: feature.geometry.coordinates[1],
                lng: feature.geometry.coordinates[0]
            });
        }
    }, [map]);
    const onLoad = react_1.default.useCallback((map) => {
        setMap(map);
        if (props.setGeocodingOptions)
            props.setGeocodingOptions({ map });
        callWithBounds(map.getBounds(), props.onMapReady);
        setPlacesInfoWindow(new google.maps.InfoWindow({
            pixelOffset: new google.maps.Size(0, -40)
        }));
    }, []);
    const onUnmount = react_1.default.useCallback(() => {
        setMap(null);
        if (props.setGeocodingOptions)
            props.setGeocodingOptions({ map: undefined });
    }, []);
    const onPositionChange = react_1.default.useCallback((e, triggerEvent) => {
        const geojsonValue = (0, GoogleMapUtils_1.geojson)(e.latLng);
        if (geojsonValue) {
            geojsonValue.properties.lastAction = triggerEvent;
            geojsonValue.properties.zoom = map.getZoom();
            geojsonValue.properties.platform = bowser_1.default.getParser(window.navigator.userAgent).getPlatformType();
        }
        changeValueAndPan(geojsonValue);
    }, [map]);
    const onZoomChange = () => {
        if (!map)
            return;
        const currentZoom = map.getZoom();
        if (currentZoom && props.maxZoom && props.maxZoom < currentZoom) {
            map.setZoom(props.maxZoom);
        }
    };
    const onBoundsChanged = () => {
        if (!map)
            return;
        callWithBounds(map.getBounds(), props.onBoundsChanged);
    };
    react_1.default.useEffect(() => {
        if (map) {
            map.panTo({
                lat: props.defaultCenter.lat,
                lng: props.defaultCenter.lon
            });
        }
    }, [props.defaultCenter]);
    react_1.default.useEffect(() => {
        if (!map || !props.defaultZoom)
            return;
        // If the zoom is changed, we need to zoom to that level
        const currentZoom = map.getZoom();
        if (currentZoom !== props.defaultZoom) {
            map.setZoom(props.defaultZoom);
        }
    }, [props.defaultZoom]);
    react_1.default.useEffect(() => {
        if (map && props.markers.length >= 1) {
            const maxMapBounds = new google.maps.LatLngBounds();
            if (props.maxGeocodingResultsBounds !== undefined) {
                maxMapBounds.extend(props.maxGeocodingResultsBounds[0]); // lower left
                maxMapBounds.extend(props.maxGeocodingResultsBounds[1]); // upper right
            }
            let atLeastOneMarkerInMaxBounds = false;
            const markerBounds = new google.maps.LatLngBounds();
            const boundsForMarkersInMaxBounds = new google.maps.LatLngBounds();
            props.markers.forEach((marker) => {
                const position = {
                    lat: marker.position.geometry.coordinates[1],
                    lng: marker.position.geometry.coordinates[0]
                };
                markerBounds.extend(position);
                if (!maxMapBounds.isEmpty() && maxMapBounds.contains(position)) {
                    atLeastOneMarkerInMaxBounds = true;
                    boundsForMarkersInMaxBounds.extend(position);
                }
            });
            const bounds = atLeastOneMarkerInMaxBounds ? boundsForMarkersInMaxBounds : markerBounds;
            // hack for single markers: see https://stackoverflow.com/questions/3334729/google-maps-v3-fitbounds-zoom-too-close-for-single-marker
            if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
                const extendPoint1 = new google.maps.LatLng(bounds.getNorthEast().lat() + 0.001, bounds.getNorthEast().lng() + 0.001);
                const extendPoint2 = new google.maps.LatLng(bounds.getNorthEast().lat() - 0.001, bounds.getNorthEast().lng() - 0.001);
                bounds.extend(extendPoint1);
                bounds.extend(extendPoint2);
            }
            map.fitBounds(bounds);
        }
    }, [props.shouldFitBounds]);
    react_1.default.useEffect(() => {
        if (!placesInfoWindow) {
            return;
        }
        placesInfoWindow.close();
        if (props.infoWindow) {
            placesInfoWindow.setContent(props.infoWindow.content);
            placesInfoWindow.setPosition({
                lat: props.infoWindow.position.geometry.coordinates[1],
                lng: props.infoWindow.position.geometry.coordinates[0]
            });
            placesInfoWindow.open(map);
        }
    }, [props.infoWindow]);
    return isLoaded ? (react_1.default.createElement(api_1.GoogleMap, { mapContainerStyle: {
            boxSizing: 'border-box',
            position: 'relative',
            width: '100%',
            height: props.height || '40rem',
            border: '1px solid rgba(0,0,0,0.2)'
        }, center: center, zoom: props.defaultZoom || 10, options: {
            zoomControl: true,
            cameraControl: false,
            zoomControlOptions: {
                position: google.maps.ControlPosition.RIGHT_BOTTOM
            }
        }, onLoad: onLoad, onUnmount: onUnmount, onClick: (e) => onPositionChange(e, 'mapClicked'), onZoomChanged: onZoomChange, onBoundsChanged: onBoundsChanged, clickableIcons: false }, props.markers.map((markerData, index) => (react_1.default.createElement(api_1.Marker, { key: `marker${index}${markerData.position.geometry.coordinates[0]}_${markerData.position.geometry.coordinates[1]}`, position: (0, GoogleMapUtils_1.toLatLng)(markerData.position), onDragEnd: markerData.draggable ? (e) => onPositionChange(e, 'markerDragged') : undefined, draggable: markerData.draggable, icon: {
            url: markerData.icon.url,
            size: new google.maps.Size(markerData.icon.size[0], markerData.icon.size[1]),
            scaledSize: new google.maps.Size(markerData.icon.size[0], markerData.icon.size[1])
        }, onClick: markerData.onClick }))))) : (react_1.default.createElement(InputLoading_1.default, null));
};
exports.default = (0, react_i18next_1.withTranslation)()(InputMapGoogle);
//# sourceMappingURL=InputMapGoogle.js.map